// pages/reports/Reports.jsx
// One place for order / customer / karigar / payment / receipt reports, all derived
// client-side from the existing list APIs. Payments are money paid out to karigars;
// receipts are money received against orders. Shared inclusive date-range filter,
// per-tab KPI tiles + table, CSV export and print (print chrome hidden via .no-print).
import { useEffect, useMemo, useState } from "react";
import {
  ordersAPI, customersAPI, karigarsAPI, paymentsAPI, receiptsAPI,
} from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { Loading, EmptyState } from "../../components/ui";
import { fmt } from "../../utils/helpers";

const TABS = [
  { key: "orders",    label: "📋 Orders" },
  { key: "customers", label: "👥 Customers" },
  { key: "karigars",  label: "🧵 Karigars" },
  { key: "payments",  label: "💳 Payments" },
  { key: "receipts",  label: "🧾 Receipts" },
];

const money = (n) => `₹${Number(n || 0).toLocaleString()}`;

// CSV: escape any cell that contains a quote, comma or newline.
function toCSV(columns, rows) {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.map((c) => esc(c.header)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.get(r))).join(",")).join("\n");
  return `${head}\n${body}`;
}

export default function Reports() {
  const toast = useToast();

  const [tab, setTab]     = useState("orders");
  const [loading, setLoading] = useState(true);
  const [from, setFrom]   = useState("");
  const [to, setTo]       = useState("");
  const [data, setData]   = useState({ orders: [], customers: [], karigars: [], payments: [], receipts: [] });

  useEffect(() => {
    setLoading(true);
    // allSettled so one unconfigured endpoint (e.g. payments before specs land)
    // can't blank the whole page — failed lists just come back empty.
    Promise.allSettled([
      ordersAPI.list(),
      customersAPI.list(),
      karigarsAPI.list(),
      paymentsAPI.list(),
      receiptsAPI.list(),
    ]).then(([o, c, k, p, r]) => {
      const val = (res) => (res.status === "fulfilled" && Array.isArray(res.value) ? res.value : []);
      setData({
        orders: val(o), customers: val(c), karigars: val(k),
        payments: val(p), receipts: val(r),
      });
      if ([o, c, k, p, r].some((res) => res.status === "rejected")) {
        toast("Some reports could not be loaded", "error");
      }
      setLoading(false);
    });
  }, []);

  // Inclusive range; undated rows (customers/karigars) always pass.
  const inRange = useMemo(() => {
    const lo = from ? new Date(from + "T00:00:00") : null;
    const hi = to   ? new Date(to   + "T00:00:00") : null;
    return (dateStr) => {
      if (!dateStr) return true;
      const d = new Date(dateStr + "T00:00:00");
      if (isNaN(d)) return true;
      if (lo && d < lo) return false;
      if (hi && d > hi) return false;
      return true;
    };
  }, [from, to]);

  const view = useMemo(() => buildView(tab, data, inRange), [tab, data, inRange]);

  const handleExport = () => {
    if (!view.rows.length) return toast("Nothing to export", "error");
    const csv  = toCSV(view.columns, view.rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `${tab}-report.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast("CSV downloaded");
  };

  const rangeLabel =
    from && to ? `${fmt(from)} – ${fmt(to)}`
    : from     ? `From ${fmt(from)}`
    : to       ? `Until ${fmt(to)}`
    : "All time";

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-sub">
            {TABS.find((t) => t.key === tab)?.label.replace(/^\S+\s/, "")} · {rangeLabel}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }} className="no-print">
          <button className="btn btn-ghost" onClick={() => window.print()}>🖨 Print</button>
          <button className="btn btn-secondary" onClick={handleExport}>⬇ Export CSV</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="section-tabs no-print">
        {TABS.map((t) => (
          <div
            key={t.key}
            className={`section-tab${tab === t.key ? " active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </div>
        ))}
      </div>

      {/* Date range filter */}
      <div className="no-print" style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap" }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">From</label>
          <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">To</label>
          <input type="date" className="form-input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        {(from || to) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(""); setTo(""); }}>✕ Clear dates</button>
        )}
        {(tab === "customers" || tab === "karigars") && (from || to) && (
          <span className="td-muted" style={{ fontSize: 12 }}>Date filter doesn’t apply to this report.</span>
        )}
      </div>

      {loading ? (
        <Loading />
      ) : (
        <>
          {/* KPI tiles */}
          <div className="stats-grid">
            {view.kpis.map((k, i) => (
              <div key={i} className="stat-card">
                <div className="stat-card-icon">{k.icon}</div>
                <div className="stat-card-value">{k.value}</div>
                <div className="stat-card-label">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>{view.columns.map((c) => <th key={c.header}>{c.header}</th>)}</tr>
                </thead>
                <tbody>
                  {view.rows.length === 0 ? (
                    <tr>
                      <td colSpan={view.columns.length}>
                        <EmptyState icon="📊" title="No data for this report" subtitle="Try widening the date range." />
                      </td>
                    </tr>
                  ) : (
                    view.rows.map((row, i) => (
                      <tr key={row.id ?? i}>
                        {view.columns.map((c) => (
                          <td key={c.header}>{(c.render ?? c.get)(row)}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Per-tab derivation: KPIs + shared column defs (table + CSV) ──────────────
function buildView(tab, data, inRange) {
  if (tab === "orders") {
    const rows = data.orders.filter((o) => inRange(o.orderDate));
    const completed = rows.filter((o) => o.status === "Completed").length;
    const value = rows.reduce((s, o) => s + (o.amount || 0), 0);
    return {
      kpis: [
        { icon: "📋", label: "Orders",    value: rows.length },
        { icon: "✅", label: "Completed", value: completed },
        { icon: "⏳", label: "Pending",   value: rows.length - completed },
        { icon: "💰", label: "Total Value", value: money(value) },
      ],
      columns: [
        { header: "Order ID", get: (o) => o.id },
        { header: "Customer", get: (o) => o.customerName || "" },
        { header: "Date",     get: (o) => o.orderDate || "", render: (o) => fmt(o.orderDate) },
        { header: "Status",   get: (o) => o.status || "" },
        { header: "Amount",   get: (o) => o.amount || 0, render: (o) => money(o.amount) },
      ],
      rows,
    };
  }

  if (tab === "customers") {
    const rows = data.customers;
    const balance = rows.reduce((s, c) => s + (c.balance || 0), 0);
    const withBal = rows.filter((c) => (c.balance || 0) !== 0).length;
    return {
      kpis: [
        { icon: "👥", label: "Customers",    value: rows.length },
        { icon: "📒", label: "With Balance", value: withBal },
        { icon: "💰", label: "Total Balance", value: money(balance) },
      ],
      columns: [
        { header: "ID",      get: (c) => c.id },
        { header: "Name",    get: (c) => c.name || "" },
        { header: "Phone",   get: (c) => c.phone || "" },
        { header: "City",    get: (c) => c.address || "" },
        { header: "Balance", get: (c) => c.balance || 0, render: (c) => money(c.balance) },
      ],
      rows,
    };
  }

  if (tab === "karigars") {
    const rows = data.karigars;
    const payable = rows.reduce((s, k) => s + (k.balance || 0), 0);
    return {
      kpis: [
        { icon: "🧵", label: "Karigars",      value: rows.length },
        { icon: "💰", label: "Total Balance", value: money(payable) },
      ],
      columns: [
        { header: "ID",        get: (k) => k.id },
        { header: "Name",      get: (k) => k.name || "" },
        { header: "Work Type", get: (k) => k.workType || "" },
        { header: "Phone",     get: (k) => k.phone || "" },
        { header: "Balance",   get: (k) => k.balance || 0, render: (k) => money(k.balance) },
      ],
      rows,
    };
  }

  if (tab === "payments") {
    const rows = data.payments.filter((p) => inRange(p.date));
    const total = rows.reduce((s, p) => s + (p.amount || 0), 0);
    return {
      kpis: [
        { icon: "💳", label: "Payments", value: rows.length },
        { icon: "💰", label: "Paid Out", value: money(total) },
      ],
      columns: [
        { header: "Payment ID", get: (p) => p.id ?? "" },
        { header: "Karigar",    get: (p) => p.karigarName || "" },
        { header: "Amount",     get: (p) => p.amount || 0, render: (p) => money(p.amount) },
        { header: "Mode",       get: (p) => p.mode || "" },
        { header: "Date",       get: (p) => p.date || "", render: (p) => fmt(p.date) },
      ],
      rows,
    };
  }

  // receipts
  const rows = data.receipts.filter((r) => inRange(r.date));
  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  return {
    kpis: [
      { icon: "🧾", label: "Receipts", value: rows.length },
      { icon: "💰", label: "Received", value: money(total) },
    ],
    columns: [
      { header: "Receipt ID", get: (r) => r.id ?? "" },
      { header: "Order",      get: (r) => r.orderId ?? "" },
      { header: "Customer",   get: (r) => r.customerName || "" },
      { header: "Amount",     get: (r) => r.amount || 0, render: (r) => money(r.amount) },
      { header: "Mode",       get: (r) => r.mode || "" },
      { header: "Date",       get: (r) => r.date || "", render: (r) => fmt(r.date) },
    ],
    rows,
  };
}
